// ─────────────────────────────────────────────────────────────────────────────
// HolidayManagement.jsx — Route: /hr/attendance/holidays
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from "react";
import { Plus, Edit, Trash2, CalendarDays, ArrowLeft, Save, X } from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import PageLoader from "../../../helpers/PageLoader";
import SummaryApi from "../../../common/SummaryApi";
import api from "../../../common/apiClient";

const TYPE_COLORS = {
  national: "bg-blue-100 text-blue-700",
  regional: "bg-green-100 text-green-700",
  org_specific: "bg-purple-100 text-purple-700",
  optional: "bg-gray-100 text-gray-600",
};

const HolidayForm = ({ holiday, onSave, onClose }) => {
  const [form, setForm] = useState(holiday || { name: "", date: "", type: "national", description: "" });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.date) return toast.error("Name and date are required");
    setSaving(true);
    try {
      const res = holiday?._id
        ? await api.put(SummaryApi.updateHoliday.url(holiday._id), form)
        : await api.post(SummaryApi.createHoliday.url, form);
      toast.success(`Holiday ${holiday ? "updated" : "added"}`);
      onSave(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 top-[57px] md:top-[65px] z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 transition-opacity" onClick={onClose} />

      {/* Off-canvas Panel */}
      <div className="absolute inset-y-0 right-0 max-w-full flex">
        <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col animate-slide-in">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="font-bold text-gray-800 text-lg">{holiday ? "Edit Holiday" : "Add Holiday"}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Form Content */}
          <form id="holiday-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Holiday Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Pongal"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-300"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
              <input
                type="date"
                value={form.date ? form.date.substring(0, 10) : ""}
                onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-300"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-300"
              >
                <option value="national">National</option>
                <option value="regional">Regional</option>
                <option value="org_specific">Organization Specific</option>
                <option value="optional">Optional</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea
                rows="3"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Optional note about the holiday"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-300 resize-none"
              />
            </div>
          </form>

          {/* Footer */}
          <div className="p-6 border-t bg-gray-50 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-white border border-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="holiday-form"
              disabled={saving}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-red-100"
            >
              {saving ? "Saving…" : (
                <>
                  <Save className="w-4 h-4" />
                  {holiday ? "Update" : "Add Holiday"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export function HolidayManagement() {
  const navigate = useNavigate();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [formState, setFormState] = useState({ open: false, holiday: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${SummaryApi.getAllHoliday.url}?year=${year}`);
      setHolidays(res.data.data || []);
    } catch {
      toast.error("Failed to load holidays");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = (saved) => {
    setHolidays((prev) =>
      prev.find((h) => h._id === saved._id)
        ? prev.map((h) => (h._id === saved._id ? saved : h))
        : [...prev, saved].sort((a, b) => new Date(a.date) - new Date(b.date))
    );
    setFormState({ open: false, holiday: null });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this holiday?")) return;
    try {
      await api.delete(SummaryApi.deleteHoliday.url(id));
      setHolidays((prev) => prev.filter((h) => h._id !== id));
      toast.success("Holiday deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="p-4">
      {/* Optimized Header */}
      <div className="flex flex-row justify-between items-center mb-6 gap-2">
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg bg-red-50 border border-red-100 hover:border-red-500 text-red-600 p-1.5 sm:p-2 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-red-100 rounded-lg hidden xs:block">
              <CalendarDays className="w-5 h-5 text-red-600" />
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 whitespace-nowrap">
              Holiday<span className="hidden sm:inline"> Calendar</span>
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-red-100"
          >
            {[2024, 2025, 2026, 2027].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={() => setFormState({ open: true, holiday: null })}
            className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-pink-600 text-white p-2 sm:px-4 sm:py-2 rounded-lg hover:shadow-lg transition-all flex-shrink-0"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:block text-sm font-semibold">Add Holiday</span>
          </button>
        </div>
      </div>

      {loading ? (
        <PageLoader />
      ) : holidays.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
          <CalendarDays className="w-16 h-16 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No holidays added for {year}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {holidays.map((h) => (
            <div
              key={h._id}
              className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between gap-4 hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="text-center shrink-0 bg-red-50 rounded-lg px-3 py-1.5 border border-red-100">
                  <p className="text-[10px] text-red-500 font-bold uppercase">{format(new Date(h.date), "MMM")}</p>
                  <p className="text-xl font-bold text-red-600 leading-none my-0.5">{format(new Date(h.date), "dd")}</p>
                  <p className="text-[10px] text-gray-400 font-medium">{format(new Date(h.date), "EEE")}</p>
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-gray-800 truncate">{h.name}</p>
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md mt-1 inline-block uppercase tracking-wider ${TYPE_COLORS[h.type]}`}>
                    {h.type.replace("_", " ")}
                  </span>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => setFormState({ open: true, holiday: h })}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(h._id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {formState.open && (
        <HolidayForm
          holiday={formState.holiday}
          onSave={handleSave}
          onClose={() => setFormState({ open: false, holiday: null })}
        />
      )}
    </div>
  );
}